/**
 * NavigationHistoryContext — Smart Back Navigation
 *
 * PHILOSOPHY: "Back" takes users to where they CAME FROM. It unwinds real
 * browser history when the previous in-app page is reachable that way (so the
 * browser's own Back button stays consistent and the restored URL carries the
 * user's sort/filter state), and falls back to the logical parent route for
 * deep links, fresh tabs and external referrers.
 *
 * The decision logic lives in `src/lib/navStack.ts` as a pure reducer so it can
 * be unit-tested. This file is only the React binding.
 *
 * IMPORTANT FOR FUTURE DEVELOPMENT:
 * - Always use <BackButton> instead of hardcoded back links or navigate(-1)
 * - Breadcrumbs remain hierarchical/static for orientation
 * - URL writes for sort/filter/search MUST use `replace: true`; that is what
 * keeps them out of the back stack
 */

import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useEffect } from 'react';
import {
  loadNavStack,
  planBack,
  previousPathOf,
  readHistoryIndex,
  reduceNavStack,
  saveNavStack,
  type NavStackState,
} from '@/lib/navStack';
import { consumeDirectNav, nextMissionOrigin, type MissionOrigin } from '@/lib/missionReturn';

interface NavigationHistoryContextType {
  /** Navigate back to the previous page, or to fallbackPath if unreachable */
  goBack: (fallbackPath: string) => void;
  /** The previous path in the stack (for display), or null if empty */
  previousPath: string | null;
  /** Where a top-level page was dispatched from (not via the top bar), else null */
  missionOrigin: MissionOrigin | null;
}

const NavigationHistoryContext = createContext<NavigationHistoryContextType | null>(null);

export function NavigationHistoryProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();

  const [state, setState] = useState<NavStackState>(() => loadNavStack());
  const [missionOrigin, setMissionOrigin] = useState<MissionOrigin | null>(null);
  // Reads inside goBack must see the latest state without re-creating the
  // callback on every navigation.
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    const from = stateRef.current.current;
    const direct = consumeDirectNav();
    const next = reduceNavStack(stateRef.current, {
      action: navigationType,
      pathname: location.pathname,
      search: location.search,
      idx: readHistoryIndex(),
    });
    stateRef.current = next;
    saveNavStack(next);
    setState(next);
    setMissionOrigin((prev) =>
      nextMissionOrigin(prev, { action: navigationType, pathname: location.pathname, direct }, from),
    );
    // navigationType intentionally excluded: the location change is the event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.pathname, location.search]);

  const goBack = useCallback(
    (fallbackPath: string) => {
      const plan = planBack(stateRef.current, fallbackPath);
      if (plan.kind === 'history-back') {
        navigate(plan.delta);
        return;
      }
      // `replace` so a dead-end entry is not created for deep-link arrivals.
      navigate(plan.path, { replace: true });
    },
    [navigate],
  );

  const previousPath = previousPathOf(state);

  return (
    <NavigationHistoryContext.Provider value={{ goBack, previousPath, missionOrigin }}>
      {children}
    </NavigationHistoryContext.Provider>
  );
}

/**
 * Hook to access smart back navigation.
 * Use goBack(fallbackPath) instead of navigate(-1) or hardcoded Links.
 */
export function useNavigationHistory() {
  const ctx = useContext(NavigationHistoryContext);
  if (!ctx) {
    throw new Error('useNavigationHistory must be used within NavigationHistoryProvider');
  }
  return ctx;
}
